import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Client, Databases, Storage } from "appwrite";

// Initialize Appwrite client
const client = new Client();
client
  .setEndpoint("https://cloud.appwrite.io/v1")
  .setProject("67fbee6a001260770104");

const databases = new Databases(client);
const storage = new Storage(client);

const DATABASE_ID = "68098bc80034d68e4e64";
const COLLECTION_ID = "68098bd900226cbeb1cd";
const BUCKET_ID = "680990d6000f00f9d400";

const KnowledgePage = () => {
  const { knowledgeId } = useParams();
  const navigate = useNavigate();
  const [knowledge, setKnowledge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchKnowledge = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await databases.getDocument(
          DATABASE_ID,
          COLLECTION_ID,
          knowledgeId
        );

        // Check if knowledge post is approved and is actually a knowledge hub post
        if (!response.isApproved) {
          setError("This knowledge post is not available.");
          return;
        }

        if (response.contentType && response.contentType.toLowerCase() !== "knowledge hub") {
          setError("Content not found.");
          return;
        }

        setKnowledge(response);
      } catch (error) {
        console.error("Error fetching knowledge post:", error);
        setError("Knowledge post not found.");
      } finally {
        setLoading(false);
      }
    };

    if (knowledgeId) {
      fetchKnowledge();
    }
  }, [knowledgeId]);

  const getImageUrl = (imageId) => {
    if (!imageId) return null;
    try {
      return storage.getFileView(BUCKET_ID, imageId);
    } catch (error) {
      console.error("Error generating image URL:", error);
      return "";
    }
  };

  const handleShare = () => {
    const shareData = {
      title: `${knowledge.title} - PoemXtra Knowledge Hub`,
      text: `Check out this knowledge article: "${knowledge.title}"`,
      url: window.location.href
    };

    if (navigator.share) {
      navigator.share(shareData).catch(console.error);
    } else {
      // Fallback - copy to clipboard
      navigator.clipboard.writeText(window.location.href).then(() => {
        alert('Link copied to clipboard!');
      }).catch((err) => {
        console.error('Failed to copy: ', err);
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-yellow-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading knowledge article...</p>
        </div>
      </div>
    );
  }

  if (error || !knowledge) {
    return (
      <div className="min-h-screen bg-yellow-50 flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-lg shadow-md max-w-md">
          <div className="text-6xl mb-4">😔</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Oops!</h1>
          <p className="text-gray-600 mb-6">{error || "Knowledge post not found."}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-yellow-500 text-white px-6 py-2 rounded hover:bg-yellow-600 transition-colors"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-yellow-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-yellow-600 hover:text-yellow-700 transition-colors"
          >
            <span>←</span>
            Back to PoemXtra
          </button>
          
        </div>

        {/* Knowledge Content */}
        <article className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Image */}
          {knowledge.imageId && (
            <div className="w-full h-64 md:h-80 bg-gray-100 flex items-center justify-center">
              <img
                src={getImageUrl(knowledge.imageId)}
                alt={knowledge.title}
                className="w-full h-full object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}

          {/* Content */}
          <div className="p-6 md:p-8">
            <header className="mb-6">
              {/* Knowledge Hub badge */}
              <div className="flex items-center gap-2 mb-4">
                <span className="bg-yellow-100 text-yellow-800 text-xs font-semibold px-2.5 py-0.5 rounded-full uppercase tracking-wide flex items-center gap-1">
                  💡 Knowledge Hub
                </span>
                {knowledge.category && (
                  <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                    {knowledge.category}
                  </span>
                )}
              </div>

              <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
                {knowledge.title}
              </h1>
              {knowledge.author && (
                <p className="text-lg text-gray-600">by {knowledge.author}</p>
              )}
              <div className="text-sm text-gray-500 mt-2">
                Published on {new Date(knowledge.$createdAt).toLocaleDateString()}
              </div>
            </header>

            {/* Knowledge content */}
            <div className="prose prose-lg max-w-none">
              <div className="whitespace-pre-wrap text-gray-700 leading-relaxed text-lg">
                {knowledge.content}
              </div>
            </div>

            {/* Key Takeaways (if you have this field) */}
            {knowledge.keyTakeaways && (
              <div className="mt-8 p-4 bg-yellow-50 rounded-lg border-l-4 border-yellow-400">
                <h3 className="text-lg font-semibold text-yellow-800 mb-2">💡 Key Takeaways</h3>
                <div className="text-gray-700">
                  {knowledge.keyTakeaways}
                </div>
              </div>
            )}

            {/* Tags if available */}
            {knowledge.tags && knowledge.tags.length > 0 && (
              <div className="mt-8 pt-6 border-t border-gray-200">
                <h3 className="text-sm font-semibold text-gray-600 mb-2">Tags:</h3>
                <div className="flex flex-wrap gap-2">
                  {knowledge.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Share section */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <h3 className="text-sm font-semibold text-gray-600 mb-3">Share this knowledge:</h3>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => {
                    const text = `Check out "${knowledge.title}" on PoemXtra Knowledge Hub: ${window.location.href}`;
                    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
                  }}
                  className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 transition-colors text-sm"
                >
                  WhatsApp
                </button>
                <button
                  onClick={() => {
                    const text = `"${knowledge.title}" - Valuable knowledge from PoemXtra`;
                    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(window.location.href)}`, '_blank');
                  }}
                  className="bg-blue-400 text-white px-4 py-2 rounded hover:bg-blue-500 transition-colors text-sm"
                >
                  Twitter
                </button>
                <button
                  onClick={() => {
                    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank');
                  }}
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors text-sm"
                >
                  Facebook
                </button>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href).then(() => {
                      alert('Link copied to clipboard!');
                    }).catch((err) => {
                      console.error('Failed to copy: ', err);
                    });
                  }}
                  className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 transition-colors text-sm"
                >
                  Copy Link
                </button>
              </div>
            </div>

            {/* Related suggestion */}
            <div className="mt-8 p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold text-blue-800 mb-2">💭 Found this helpful?</h4>
              <p className="text-blue-700 text-sm">
                Check out more knowledge articles in our{' '}
                <button 
                  onClick={() => navigate('/knowledge')}
                  className="underline hover:text-blue-900"
                >
                  Knowledge Hub
                </button>{' '}
                section!
              </p>
            </div>
          </div>
        </article>

        {/* Reading time estimate */}
        {knowledge.content && (
          <div className="mt-4 text-center text-sm text-gray-500">
            Estimated reading time: {Math.max(1, Math.ceil(knowledge.content.split(' ').length / 200))} min
          </div>
        )}
      </div>
    </div>
  );
};

export default KnowledgePage;